/**
 * «Add people by name or email» (Figma 826:10635, 826:10774): live results under the field, the Editor / Guest picker
 * inside it; a click or Enter invites at once, as the app's `SharingEmailInviteSection` does.
 */
import { useEffect, useState, type KeyboardEvent, type RefObject } from 'react';
import { accessBadge, MIN_SEARCH, topInvitee, type ShareInvitee, type ShareMatch, type SharePerson, type ShareSource, type ShareState } from '../../collaboration/share/source';
import type { CollabError } from '../../state/catalogApi';
import { plainLook } from '../../collaboration/participantColors';
import { ActionMenu, type MenuItem } from '../Dropdown';
import { Icon } from '../Icon';
import { ParticipantAvatar } from './CollaborationOverlay';
import { useAlive } from './useAlive';
import { t, type TranslationKey } from '../../i18n/I18n';

const DEBOUNCE_MS = 250;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLE: Record<'editor' | 'viewer', TranslationKey> = { editor: '#CanEdit', viewer: '#CanView' };
const BADGE = { member: 'Member', pending: 'Pending' } as const satisfies Record<string, TranslationKey>;

/** `matches` null: the search failed for that term. */
type Found = { term: string; matches: ShareMatch[] | null };
type Message = { text: string; tone: 'error' | 'note' };
export type ShareSearchState = ReturnType<typeof useShareSearch>;

function inviteFailure(error: unknown): string {
  const failure = error as Partial<CollabError> | null;
  if (failure?.reason === 'limit') return failure.limit ? t('#WebShareInviteLimitOf', failure.limit) : t('#WebShareInviteFull');
  return failure?.reason === 'owner' ? t('#WebShareInviteOwner') : t('#WebShareInviteFailed');
}

export function useShareSearch(source: ShareSource, state: ShareState, input: RefObject<HTMLInputElement | null>) {
  const [term, setTerm] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('editor');
  const [found, setFound] = useState<Found>({ term: '', matches: [] });
  const [attempt, setAttempt] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const alive = useAlive();
  const wanted = term.trim(), searching = wanted.length >= MIN_SEARCH;
  useEffect(() => {
    if (!searching) return;
    const controller = new AbortController();
    const settle = (matches: ShareMatch[] | null) => { if (!controller.signal.aborted) setFound({ term: wanted, matches }); };
    const timer = setTimeout(() => { source.search(wanted, controller.signal).then(settle, () => settle(null)); }, DEBOUNCE_MS);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [source, wanted, searching, attempt]);

  const answered = found.term === wanted;
  const status = !searching ? 'idle' as const : !answered ? 'loading' as const : found.matches ? 'ready' as const : 'error' as const;
  const matches = (answered && found.matches) || [];
  const address = EMAIL.test(wanted) ? wanted.toLowerCase() : null;
  const clear = () => { setTerm(''); setMessage(null); input.current?.focus(); };
  const invite = async (invitee: ShareInvitee) => {
    if (inviting) return;
    setInviting(true); setMessage(null);
    try {
      await source.invite(invitee, role);
      if (alive.current) clear();
    } catch (failure) {
      if (alive.current) setMessage({ text: inviteFailure(failure), tone: 'error' });
    } finally {
      if (alive.current) setInviting(false);
    }
  };
  /** Enter, or a click on the address row: the top row only, or why nobody is invited. */
  const submit = () => {
    if (status !== 'ready') return;
    const top = topInvitee(matches, address, state.people);
    if (!top) { if (wanted.includes('@')) setMessage({ text: t('#WebShareCheckAddress'), tone: 'error' }); return; }
    if ('has' in top) setMessage({ text: t('#WebShareAlreadyHasAccess', top.has), tone: 'note' });
    else void invite(top.invite);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && term) { event.preventDefault(); event.stopPropagation(); clear(); return; }
    if (event.key === 'Enter') { event.preventDefault(); submit(); }
  };
  return { term, setTerm: (value: string) => { setTerm(value); setMessage(null); }, role, setRole, status, matches, wanted, address, inviting, message,
    invite, submit, clear, onKeyDown, retry: () => { setFound({ term: '', matches: [] }); setAttempt(count => count + 1); } };
}

export function ShareSearchField({ search, input, enabled }: { search: ShareSearchState; input: RefObject<HTMLInputElement | null>; enabled: boolean }) {
  const roles: MenuItem[] = (['editor', 'viewer'] as const).map(role => ({ id: role, label: t(ROLE[role]), checked: search.role === role, onSelect: () => search.setRole(role) }));
  return <div className="share-search">
    <div className="share-field">
      <Icon name="searchSmall" size={16} />
      <input ref={input} type="search" placeholder={t('#SearchByNameOrEmail')} aria-label={t('#SearchByNameOrEmail')} disabled={!enabled} value={search.term}
        aria-busy={search.inviting} autoComplete="off" spellCheck={false} onChange={event => search.setTerm(event.target.value)} onKeyDown={search.onKeyDown} />
      {search.term && <button type="button" className="share-field-clear" aria-label={t('#WebShareClearSearch')} data-tip={t('#WebShareClearSearch')} onClick={search.clear}>
        <Icon name="closeX" size={16} /></button>}
      {enabled && <ActionMenu label={t('#WebShareInviteRole')} width={180} align="end" items={roles} triggerClassName="share-invite-role"
        trigger={<><span>{t(ROLE[search.role])}</span><Icon name="chevronDown" size={16} /></>} />}
    </div>
    {search.message && <p className={'share-search-message' + (search.message.tone === 'error' ? ' is-error' : '')} role={search.message.tone === 'error' ? 'alert' : 'status'}>
      {search.message.text}</p>}
  </div>;
}

export function ShareSearchResults({ search, people }: { search: ShareSearchState; people: readonly SharePerson[] }) {
  const { status, matches, wanted, address } = search;
  if (status === 'error') return <div className="share-failure" role="alert"><p>{t('#WebShareSearchFailed')}</p>
    <button type="button" className="share-text-button" onClick={search.retry}>{t('Retry')}</button></div>;
  if (status === 'loading') return <ul className="share-people" aria-hidden>{[0, 1].map(index => <li key={index} className="share-person is-skeleton"><i /><b /></li>)}</ul>;
  const byAddress = address && !matches.length, known = byAddress ? people.find(person => !person.visitor && person.email?.toLowerCase() === address) : undefined;
  if (!matches.length && !byAddress) return <p className="share-empty" role="status">{t('#WebShareNoResults', wanted)}</p>;
  return <ul className="share-people share-results" aria-label={t('#SearchByNameOrEmail')}>
    {matches.map(match => {
      const badge = accessBadge(people, match.id);
      return <li key={match.id}><button type="button" className="share-person share-result" disabled={!!badge || search.inviting} onClick={() => void search.invite({ id: match.id })}>
        <span className="share-avatar"><ParticipantAvatar name={match.name} look={plainLook(match.id, 'editor', match.avatar)} size={36} /></span>
        <span className="share-person-name"><span>{match.name}</span></span>
        {badge ? <span className={'share-badge' + (badge === 'member' ? ' is-member' : '')}>{t(BADGE[badge])}</span> : <span className="share-result-hint">{t('#InviteNormal')}</span>}
      </button></li>;
    })}
    {byAddress && <li><button type="button" className="share-person share-result" disabled={search.inviting} onClick={search.submit}>
      <span className="share-invite-avatar" aria-hidden>{address.slice(0, 1).toUpperCase()}</span>
      <span className="share-person-name"><span>{address}</span></span>
      {known ? <span className="share-badge">{t(known.pending ? 'Pending' : 'Member')}</span> : <span className="share-result-hint">{t('#WebShareInviteByEmail')}</span>}
    </button></li>}
  </ul>;
}
